import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Drawer,
	IconButton,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseRounded } from "@mui/icons-material";
import {
	Bar,
	BarChart,
	ResponsiveContainer,
	Tooltip as TooltipRecharts,
	XAxis,
} from "recharts";
import dayjs from "dayjs";
import IconeTipoAlerta from "../iconeTipoAlerta";
import { buscarIncidentesEquipamento } from "../context/apiIncidentes";
import { agruparAlertasPorDia } from "../../../utils/centralDeAlertas/correlacao";
import {
	SEVERIDADES,
	LABEL_SEVERIDADE,
	LABEL_TIPO_EVENTO,
	formatarDuracaoCurta,
	formatarDuracaoMinutos,
} from "../../../utils/centralDeAlertas/labels";
import {
	corSeveridade,
	corStatusIncidente,
	corTipoEvento,
	corCategoriaInteligente,
} from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Painel lateral "Analisar padrão" (ação da detecção de falha recorrente nos Alertas
// inteligentes): análise dos alertas de UM equipamento nos últimos 30 dias,
// independente do período filtrado na tela — padrão se enxerga com
// histórico, não com a janela curta do dashboard. Os dados vêm da mesma rota
// real /find/centralAlertas/incidentes (ver buscarIncidentesEquipamento).

const JANELA_ANALISE_DIAS = 30;
// Skeletons espelham o layout real: 5 indicadores, gráfico e linhas da lista.
const INDICADORES_SKELETON = 5;
const LINHAS_SKELETON = 6;

const FAIXAS_HORARIO = [
	{ rotulo: "madrugada (0h–6h)", inicio: 0, fim: 6 },
	{ rotulo: "manhã (6h–12h)", inicio: 6, fim: 12 },
	{ rotulo: "tarde (12h–18h)", inicio: 12, fim: 18 },
	{ rotulo: "noite (18h–24h)", inicio: 18, fim: 24 },
];

function media(valores) {
	const validos = (valores || []).filter(
		(valor) => valor !== null && valor !== undefined && !Number.isNaN(valor),
	);
	if (!validos.length) return null;
	return validos.reduce((soma, valor) => soma + valor, 0) / validos.length;
}

// `erpId`: ERP do painel (o histórico do equipamento é dos clientes dele).
export default function AnalisePadrao({
	aberto,
	equipamento,
	cliente,
	onFechar,
	erpId,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);
	const [incidentes, setIncidentes] = useState([]);
	const [carregando, setCarregando] = useState(true);

	const equipamentoId = equipamento?.id || null;

	// Busca o histórico ao abrir (e refaz se abrir para outro equipamento).
	useEffect(() => {
		if (!aberto || !equipamentoId) return undefined;
		const controller = new AbortController();
		setCarregando(true);
		setIncidentes([]);

		buscarIncidentesEquipamento({
			equipamentoId,
			inicio: dayjs().subtract(JANELA_ANALISE_DIAS, "day"),
			fim: dayjs(),
			erpId,
			signal: controller.signal,
		})
			.then((resultado) => {
				if (!controller.signal.aborted) setIncidentes(resultado);
			})
			.catch((error) => {
				if (!controller.signal.aborted) {
					console.error(
						"Erro ao buscar histórico do equipamento para análise de padrão:",
						error,
					);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setCarregando(false);
			});

		return () => controller.abort();
	}, [aberto, equipamentoId]);

	const analise = useMemo(() => {
		const lista = incidentes || [];
		if (!lista.length) return null;

		// Alertas em ordem cronológica para medir os intervalos entre eles.
		const alertasAsc = lista
			.map((incidente) => dayjs(incidente.dataHoraQueda).valueOf())
			.sort((a, b) => a - b);
		const intervalosMin = alertasAsc
			.slice(1)
			.map((alerta, indice) => (alerta - alertasAsc[indice]) / 60000);

		const mttrs = lista.map((incidente) => incidente.mttr);

		// Faixa de horário com mais alertas.
		const contagemPorFaixa = FAIXAS_HORARIO.map((faixa) => ({
			...faixa,
			alertas: lista.filter((incidente) => {
				const hora = dayjs(incidente.dataHoraQueda).hour();
				return hora >= faixa.inicio && hora < faixa.fim;
			}).length,
		}));
		const faixaMaisComum = contagemPorFaixa.reduce(
			(maior, faixa) => (faixa.alertas > maior.alertas ? faixa : maior),
			contagemPorFaixa[0],
		);

		return {
			totalAlertas: lista.length,
			abertosAgora: lista.filter((incidente) => incidente.aberto).length,
			mttrMedio: media(mttrs),
			maiorDuracao: mttrs.some(
				(valor) => valor !== null && valor !== undefined,
			)
				? Math.max(...mttrs.filter((valor) => valor !== null && valor !== undefined))
				: null,
			intervaloMedio: media(intervalosMin),
			faixaMaisComum: faixaMaisComum.alertas > 0 ? faixaMaisComum : null,
		};
	}, [incidentes]);

	const dadosGrafico = useMemo(
		() =>
			agruparAlertasPorDia(
				incidentes,
				dayjs().subtract(JANELA_ANALISE_DIAS, "day"),
				dayjs(),
			),
		[incidentes],
	);

	// Uma linha por alerta, com os campos já derivados — o DataGrid ordena
	// pelos valores crus (ms/minutos) e formata só na exibição.
	// Ordem padrão: alertas EM ABERTO primeiro (são os acionáveis), depois os
	// demais, ambos do mais recente para o mais antigo. Clicar num cabeçalho
	// reordena normalmente — esta é a ordem inicial, não uma trava.
	const linhasAlertas = useMemo(
		() =>
			(incidentes || [])
				.map((incidente) => ({
					id: incidente.id,
					severidade: incidente.severidade,
					alerta: incidente.alerta?.titulo || null,
					tipoAlertaChave: incidente.alerta?.tipoAlertaChave || null,
					tipoAlertaNome: incidente.alerta?.tipoAlertaNome || null,
					reconhecido: Boolean(incidente.alerta?.reconhecido),
					inicio: dayjs(incidente.dataHoraQueda).valueOf(),
					fim: incidente.recuperacao
						? dayjs(incidente.recuperacao).valueOf()
						: null,
					duracao: incidente.mttr,
					atendimento: Boolean(incidente.atendimento),
					osCriada: Boolean(incidente.os),
					osConcluida: Boolean(incidente.os?.dataHoraConclusao),
					recuperado: Boolean(incidente.recuperacao),
					aberto: Boolean(incidente.aberto),
				}))
				.sort((a, b) => {
					if (a.aberto !== b.aberto) return a.aberto ? -1 : 1;
					return b.inicio - a.inicio;
				}),
		[incidentes],
	);

	// Marcos do ciclo em segmentos — mesma ordem e cores da linha do tempo
	// do incidente; rótulo no Tooltip (nunca depender só da cor).
	const marcosDaLinha = (linha) => [
		{ chave: "queda", rotulo: LABEL_TIPO_EVENTO.queda, presente: true, cor: corTipoEvento(theme, "queda") },
		{ chave: "reconhecimento", rotulo: "Reconhecimento", presente: linha.reconhecido, cor: corStatusIncidente(theme, "reconhecido") },
		{ chave: "atendimento_aberto", rotulo: LABEL_TIPO_EVENTO.atendimento_aberto, presente: linha.atendimento, cor: corTipoEvento(theme, "atendimento_aberto") },
		{ chave: "os_criada", rotulo: LABEL_TIPO_EVENTO.os_criada, presente: linha.osCriada, cor: corTipoEvento(theme, "os_criada") },
		{ chave: "os_concluida", rotulo: LABEL_TIPO_EVENTO.os_concluida, presente: linha.osConcluida, cor: corTipoEvento(theme, "os_concluida") },
		{ chave: "recuperacao", rotulo: LABEL_TIPO_EVENTO.recuperacao, presente: linha.recuperado, cor: corTipoEvento(theme, "recuperacao") },
	];

	// KPIs compactos: valor na cor semântica + rótulo curto.
	const indicadores = analise
		? [
			{ rotulo: "Alertas", valor: String(analise.totalAlertas), cor: corTipoEvento(theme, "queda") },
			{
				rotulo: "Abertos agora",
				valor: String(analise.abertosAgora),
				cor: analise.abertosAgora ? corSeveridade(theme, "critico") : theme.palette.text.primary,
			},
			{ rotulo: "MTTR médio", valor: formatarDuracaoMinutos(analise.mttrMedio), cor: corTipoEvento(theme, "recuperacao") },
			{ rotulo: "Maior duração", valor: formatarDuracaoMinutos(analise.maiorDuracao), cor: corSeveridade(theme, "alto") },
			{ rotulo: "Intervalo médio", valor: formatarDuracaoMinutos(analise.intervaloMedio), cor: corCategoriaInteligente(theme, "falha_recorrente") },
		]
		: [];

	const renderConteudo = () => {
		if (carregando) {
			return (
				<Box sx={styles.conteudo}>
					<Box sx={styles.linhaIndicadores}>
						{Array.from({ length: INDICADORES_SKELETON }).map((_, indice) => (
							<Skeleton
								key={`skeleton-indicador-${indice}`}
								variant="rounded"
								sx={styles.skeletonIndicador}
							/>
						))}
					</Box>
					<Skeleton variant="rounded" sx={styles.skeletonGrafico} />
					<Box sx={styles.skeletonTabela}>
						{Array.from({ length: LINHAS_SKELETON }).map((_, indice) => (
							<Skeleton
								key={`skeleton-linha-${indice}`}
								variant="rounded"
								sx={styles.skeletonLinha}
							/>
						))}
					</Box>
				</Box>
			);
		}

		if (!incidentes.length) {
			return (
				<Box sx={styles.estadoVazio}>
					<Typography color="text.secondary">
						{`Nenhum alerta registrado nos últimos ${JANELA_ANALISE_DIAS} dias.`}
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={styles.conteudo}>
				<Box sx={styles.linhaIndicadores}>
					{indicadores.map(({ rotulo, valor, cor }) => (
						<Box key={rotulo} sx={styles.indicador}>
							<Typography sx={styles.valorIndicador(cor)}>{valor}</Typography>
							<Typography sx={styles.rotuloIndicador}>{rotulo}</Typography>
						</Box>
					))}
				</Box>

				{analise?.faixaMaisComum && (
					<Box sx={styles.observacao}>
						{`Os alertas se concentram na ${analise.faixaMaisComum.rotulo}: ${analise.faixaMaisComum.alertas} de ${analise.totalAlertas} no período.`}
					</Box>
				)}

				<Box>
					<Typography sx={styles.tituloSecao}>Alertas por dia</Typography>
					<Box sx={styles.areaGrafico}>
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={dadosGrafico} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
								<XAxis
									dataKey="dia"
									tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
									minTickGap={24}
									axisLine={{ stroke: theme.palette.divider }}
									tickLine={false}
								/>
								<TooltipRecharts
									formatter={(valor, severidade) => [
										valor,
										LABEL_SEVERIDADE[severidade] || severidade,
									]}
									contentStyle={styles.tooltipGrafico}
									cursor={styles.cursorGrafico}
								/>
								{SEVERIDADES.map((severidade) => (
									<Bar
										key={severidade}
										dataKey={severidade}
										stackId="alertas"
										fill={corSeveridade(theme, severidade)}
										radius={[2, 2, 0, 0]}
									/>
								))}
							</BarChart>
						</ResponsiveContainer>
					</Box>
				</Box>

				<Box>
					<Typography sx={styles.tituloSecao}>
						{`Alertas do período (${incidentes.length})`}
					</Typography>
					{/* Lista compacta: em aberto primeiro, depois do mais recente
					    para o mais antigo. */}
					{linhasAlertas.map((linha) => (
						<Box key={linha.id} sx={styles.linhaAlerta}>
							<Tooltip title={LABEL_SEVERIDADE[linha.severidade] || ""} arrow>
								<Box sx={styles.pontoSeveridade(corSeveridade(theme, linha.severidade))} />
							</Tooltip>
							<Box sx={styles.linhaTituloAlerta}>
								<IconeTipoAlerta
									chave={linha.tipoAlertaChave}
									nome={linha.tipoAlertaNome}
									sx={{ color: corSeveridade(theme, linha.severidade), fontSize: "16px" }}
								/>
								<Tooltip title={linha.alerta || ""} arrow>
									<Typography sx={styles.tituloAlertaTabela}>
										{linha.alerta || "—"}
									</Typography>
								</Tooltip>
							</Box>
							<Typography sx={styles.textoNumero(null)}>
								{dayjs(linha.inicio).format("DD/MM HH:mm")}
							</Typography>
							<Typography
								sx={styles.textoNumero(
									linha.aberto ? corSeveridade(theme, "critico") : null,
								)}
							>
								{linha.aberto ? "em aberto" : formatarDuracaoCurta(linha.duracao)}
							</Typography>
							<Box sx={styles.barraCiclo}>
								{marcosDaLinha(linha).map(({ chave, rotulo, presente, cor }) => (
									<Tooltip
										key={chave}
										title={presente ? rotulo : `${rotulo} — não registrado`}
										arrow
									>
										<Box sx={styles.segmentoCiclo(presente ? cor : null)} />
									</Tooltip>
								))}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<Drawer
			anchor="right"
			open={aberto}
			onClose={onFechar}
			PaperProps={{ sx: styles.painelDrawer }}
		>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.titulo}>
					<Typography variant="caption" sx={styles.sobretitulo}>
						{`Análise de padrão · ${JANELA_ANALISE_DIAS} dias`}
					</Typography>
					<Typography sx={styles.nomeEquipamento}>
						{equipamento?.nome || "—"}
					</Typography>
					<Typography variant="caption" sx={styles.subtitulo}>
						{cliente?.nome || "—"}
					</Typography>
				</Box>
				<Box sx={styles.acoesCabecalho}>
					<MetricInfo theme={theme} guide={GUIDES_CENTRAL.analisePadrao} />
					<IconButton size="small" aria-label="Fechar" onClick={onFechar} sx={styles.botaoFechar}>
						<CloseRounded fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			<Box sx={styles.corpoDrawer}>{renderConteudo()}</Box>
		</Drawer>
	);
}
